'use client';

import { Skeleton, Stack, Theme, Typography } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import CardContainer from '../CardContainer/CardContainer';
import NameValueDisplay from '../NameValueDisplay';
import PermissionBlock, { TPermissionBlockProps } from '../PermissionBlock/PermissionBlock';

export type TIndividualDetailsProps = {
  /** Full name as one string. The card shows what the customer told us, not a document's version. */
  name?: string;
  /** Already formatted. This component does no date formatting, so every field agrees on a format. */
  dateOfBirth?: string;
  email?: string;
  preferredContactNumber?: string;
  /** A node, because an address renders as several lines and one caller already has a formatter. */
  address?: ReactNode;
  /**
   * The customer's standing permission.
   *
   * Omitting it is the same as passing `{ status: 'none' }`: the field still renders, with a dash.
   * It is never removed from the card. Every other field here shows `-` when it has no value, and
   * a permission field that disappeared made one field behave unlike its five neighbours while
   * reflowing the card's last row.
   */
  permission?: TPermissionBlockProps;
  /**
   * Draws the five identity values as placeholders while the customer is being fetched.
   *
   * Separate from the permission's own loading state, because the two have different owners and do
   * not arrive together: these five come from the customer object the page fetches, the permission
   * from its own endpoint. One flag for both would either skeleton details already in memory or
   * claim a permission that has not landed.
   *
   * **The labels are not skeletoned**, only the values. Which fields this card shows is known before
   * any request, so hiding the labels would conceal something we already have.
   *
   * Without this the values fall back to `-`, which states that the customer has not given us their
   * name or date of birth. That is a claim about the customer, and a request in flight cannot
   * support it.
   */
  isLoadingDetails?: boolean;
  'data-testid'?: string;
};

/** What a field shows when the customer has not given us that detail. */
const MISSING_VALUE = '-';

/**
 * One field's share of the row: two per row, at any width the card is given.
 *
 * `1 1 40%` rather than a track count, so the count follows from the arithmetic: two 40% items plus
 * a gap fit a row and three cannot, and each still grows to fill what is left. Below roughly twice
 * the field's own comfortable width they wrap to one per row on their own, with no breakpoint,
 * which is what this card needs: it sits in a row beside other cards, so what matters is the room
 * it actually gets rather than how wide the window is.
 */
const FIELD = { flex: '1 1 40%', minWidth: 0 };

/**
 * The customer details card: six fields in two columns.
 *
 * **Two columns, not one.** The previous card ran five fields down a single column, which left the
 * card tall and mostly empty beside the confidence cards next to it.
 *
 * Two is a fixed count here, expressed as a wrapping row of `flex: 1 1 40%` fields rather than as a
 * grid. An earlier attempt used `repeat(auto-fit, minmax(200px, 1fr))`, reasoning that 200px was the
 * narrowest a field could sit at, and shipped four to five columns: `auto-fit` reads a minimum as
 * permission to add as many tracks as fit, so at a normal desktop width the card laid its six fields
 * out in one or two long rows and read as a strip rather than a card. Two 40% items plus a gap fit a
 * row and three cannot, so the count is a property of the rule rather than of the width.
 *
 * **Permission is the sixth field, not a separate block.** It was missing from the redesign
 * entirely and was restored in review: it exists on the live page today and a client relies on it,
 * so dropping it silently would have removed a record rather than redesigned one. Folding it in as a
 * field rather than giving it its own card keeps it beside the details it qualifies.
 *
 * **The portrait is deliberately not here.** It moved to the biometric check card, which is why
 * these fields own the card's full width.
 *
 * **Values arrive formatted.** This renders what it is given and formats nothing, so a caller cannot
 * end up with two date formats in one card by using two different helpers.
 */
export default function IndividualDetails({
  name,
  dateOfBirth,
  email,
  preferredContactNumber,
  address,
  permission,
  isLoadingDetails,
  'data-testid': dataTestId,
}: Readonly<TIndividualDetailsProps>): ReactElement {
  // One placeholder stands in for whichever value is being awaited. Built here rather than inlined
  // five times so every field's placeholder is the same width and the card cannot end up with a
  // ragged loading state.
  const valueOf = (value: ReactNode) =>
    isLoadingDetails ? <Skeleton variant="text" width="70%" /> : value;
  return (
    <CardContainer
      data-testid={dataTestId}
      // The frozen design shows the Details card with no border on either side, measured as
      // `0px none` on both the prototype and the build: `disableBorder` opts this card out of
      // `CardContainer`'s themed 1px border, so the container's default does not add one here by
      // accident.
      disableBorder
      sx={(t: Theme) => ({ padding: t.semantic.spacing.base })}
    >
      <Stack sx={(t: Theme) => ({ gap: t.semantic.spacing.lg })}>
        <Typography variant="h6">Details</Typography>

        {/* Each field is wrapped rather than flexed directly, because `NameValueDisplay` sets
            `flex: 1` on itself: left as its own flex item it would take an equal share of the row
            whatever basis is asked for here, and the two-per-row rule would not hold. */}
        <Stack
          direction="row"
          flexWrap="wrap"
          sx={(t: Theme) => ({ gap: `${t.semantic.spacing.base} ${t.semantic.spacing.lg}` })}
        >
          {/* Deliberately not `copyable`. The prototype's Details card copies nothing, and the copy
              affordance indents the value away from its own label, so the name sat visibly out of
              line with the five fields beside it. */}
          <Stack sx={FIELD}>
            <NameValueDisplay name="Name" value={valueOf(name ?? MISSING_VALUE)} />
          </Stack>
          <Stack sx={FIELD}>
            <NameValueDisplay name="Date of Birth" value={valueOf(dateOfBirth ?? MISSING_VALUE)} />
          </Stack>
          <Stack sx={FIELD}>
            <NameValueDisplay name="Email" value={valueOf(email ?? MISSING_VALUE)} />
          </Stack>
          <Stack sx={FIELD}>
            <NameValueDisplay
              name="Preferred Contact Number"
              value={valueOf(preferredContactNumber ?? MISSING_VALUE)}
            />
          </Stack>
          {/* 40% rather than a full row, so the permission pairs with it. Given the whole width the
              address takes a row of its own and strands the permission alone in the next one,
              leaving most of the card's last row visibly empty. */}
          <Stack sx={FIELD}>
            <NameValueDisplay name="Address" value={valueOf(address ?? MISSING_VALUE)} />
          </Stack>
          {/* Always rendered, never dropped. An absent permission shows a dash, like every other
              field on this card: a field that vanishes leaves a client unsure whether the card is
              broken or the record is empty, and it made the card's last row reflow. */}
          <Stack sx={FIELD}>
            <PermissionBlock {...(permission ?? { status: 'none' })} />
          </Stack>
        </Stack>
      </Stack>
    </CardContainer>
  );
}
